/**
 * Backend error envelope returned by the ECQ API, e.g.
 * {
 *   "errors": "'Password' must contain at least one lowercase letter, ...",
 *   "code": "AUSFT02V000",
 *   "message": "Invalid data provided. Please check and resubmit."
 * }
 *
 * `errors` may be a single string, an array of strings, or a field→messages
 * map (ASP.NET ModelState style). `message` is the generic fallback text.
 */
export interface ApiErrorResponse {
  errors?: string | string[] | Record<string, string | string[]>;
  code?: string;
  message?: string;
  status?: string;
}

const DEFAULT_FALLBACK = "Something went wrong! Please try again.";

/**
 * Pick the most useful, human-readable message out of a backend error
 * response: detailed validation `errors` first, then the generic `message`,
 * then the provided fallback. Safe to call with any parsed JSON value.
 */
export function getApiErrorMessage(
  result: unknown,
  fallback: string = DEFAULT_FALLBACK
): string {
  if (!result || typeof result !== "object") return fallback;

  const { errors, message } = result as ApiErrorResponse;

  // 1) Detailed validation errors take priority.
  if (typeof errors === "string" && errors.trim()) {
    return errors.trim();
  }
  if (Array.isArray(errors)) {
    const joined = errors.filter(Boolean).join(", ");
    if (joined) return joined;
  }
  if (errors && typeof errors === "object") {
    const flat = Object.values(errors)
      .flatMap((value) => (Array.isArray(value) ? value : [value]))
      .filter(Boolean);
    if (flat.length) return flat.join(", ");
  }

  // 2) Generic message from the backend.
  if (typeof message === "string" && message.trim()) {
    return message.trim();
  }

  return fallback;
}
